/** The longest a web sign-out waits for Stytch to answer its revoke. */
const REVOKE_BUDGET_MS = 3_000;

/**
 * Where the Stytch SDK keeps this browser's sign-in on the web, with the default cookie options
 * the app creates it with (`platform/web/createWebPlatform`): the session cookies, and the state
 * it mirrors to localStorage, which every other tab's SDK follows through `storage` events. A
 * page with no session cookie starts signed out, whatever else is stored.
 */
const SESSION_COOKIES = ["stytch_session", "stytch_session_jwt"];
const SDK_STATE_PREFIX = "stytch_sdk_state_";

/** The part of the Stytch client a sign-out uses. */
interface SessionRevoker {
    session: { revoke(options?: { forceClear?: boolean }): Promise<unknown> };
}

/** Stytch's answer when the server has already ended the session: the sign-out endpoint may. */
const isAlreadyEnded = (error: unknown) =>
    (error as { error_type?: unknown } | null)?.error_type === "session_not_found";

/**
 * Forgets this browser's sign-in without Stytch: deletes the session cookies and the SDK's state,
 * so no page, here or in another tab, admits anyone from it again. The SDK of the page that
 * calls it still holds the session in memory; that page must leave by a full load.
 */
function forgetSourceSession(): void {
    for (const name of SESSION_COOKIES)
        for (const domain of ["", `; domain=${window.location.hostname}`])
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    try {
        const keys = Array.from({ length: localStorage.length }, (_, i) => localStorage.key(i));
        for (const key of keys)
            if (key?.startsWith(SDK_STATE_PREFIX) && !key.includes("::"))
                localStorage.removeItem(key);
    } catch (error) {
        console.warn("[Logout] Could not clear the stored sign-in:", error);
    }
}

/**
 * Ends this browser's own sign-in on the web, after the server ended the project sessions
 * exchanged from it. `forceClear`: whatever Stytch answers, the SDK drops the session here
 * (cookies and state), so `/authenticate` cannot see it and send the person straight back in. A
 * session the server already ended (the sign-out endpoint may end it) is a sign-out done.
 * Stytch gets `REVOKE_BUDGET_MS`; with no answer by then the sign-in is forgotten here
 * (`forgetSourceSession`) and the answer is "forgotten": the caller leaves by a full load.
 */
export async function revokeSourceSession(client: SessionRevoker): Promise<"ended" | "forgotten"> {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const late = new Promise<"late">((resolve) => {
        timer = setTimeout(() => resolve("late"), REVOKE_BUDGET_MS);
    });
    const revoked = client.session.revoke({ forceClear: true }).then(
        () => "ended" as const,
        (error: unknown) => {
            if (!isAlreadyEnded(error))
                console.warn("[Logout] Stytch did not revoke the session; cleared here:", error);
            return "ended" as const;
        },
    );
    const outcome = await Promise.race([revoked, late]).finally(() => clearTimeout(timer));
    if (outcome === "ended") return "ended";
    console.warn(
        "[Logout] Stytch did not answer the revoke in time; the sign-in is forgotten here",
    );
    forgetSourceSession();
    return "forgotten";
}
